import React, { useEffect, useState } from "react";
import { getPublicStats } from "../lib/publicStats";
import { X, UsersThree } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * JustJoinedPopup — social-proof toast bottom-left shown briefly after the
 * WelcomeLottie guide closes (or on-demand via the custom event
 * `mc68:social-proof-show`). Minimal session dedup.
 */
export default function JustJoinedPopup() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const [totalPremium, setTotalPremium] = useState(0);

  useEffect(() => {
    const trigger = async () => {
      if (typeof window === "undefined") return;
      if (sessionStorage.getItem("mc68_social_proof_shown") === "1") return;
      try {
        const r = await getPublicStats();
        const joined = Number(r?.data?.joined_week || 0);
        const premium = Number(r?.data?.premium_businesses || 0);
        const display = joined > 0 ? joined : premium;
        if (display <= 0) return;
        setCount(display > 0 ? joined : 0);
        setTotalPremium(premium);
        setVisible(true);
        sessionStorage.setItem("mc68_social_proof_shown", "1");
        setTimeout(() => setVisible(false), 9000);
      } catch (err) {
        console.debug("[JustJoinedPopup] trigger failed", err);
      }
    };
    const handler = () => trigger();
    // Auto-trigger ~1.2s after WelcomeLottie close
    window.addEventListener("mc68:welcome-closed", () => setTimeout(trigger, 1200));
    window.addEventListener("mc68:social-proof-show", handler);
    return () => {
      window.removeEventListener("mc68:welcome-closed", handler);
      window.removeEventListener("mc68:social-proof-show", handler);
    };
  }, []);

  if (!visible) return null;

  const headline = count > 0
    ? (count > 1 ? t.social_proof.joined_many : t.social_proof.joined_one).replace("{n}", count)
    : t.social_proof.trust.replace("{n}", totalPremium);

  return (
    <div
      data-testid="social-proof-toast"
      className="fixed bottom-6 left-6 z-[9998] animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <div className="flex items-start gap-3 bg-card ring-1 ring-primary/20 shadow-xl rounded-2xl px-4 py-3 max-w-sm">
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 grid place-items-center text-primary">
          <UsersThree size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-primary mb-0.5">
            {t.social_proof.community}
          </div>
          <div className="text-sm font-medium text-foreground leading-snug" data-testid="social-proof-headline">
            🎉 {headline}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.social_proof.cta}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          data-testid="social-proof-close"
          className="shrink-0 w-6 h-6 -mr-1 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
          aria-label={t.social_proof.close}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
